import { useParams, useNavigate } from 'react-router-dom';
import { useAppStore } from '../hooks/useAppStore';
import { ArrowLeft, Lock, Eye, EyeOff, ChevronDown } from 'lucide-react';
import * as Icons from 'lucide-react';
import SignalBar from '../components/SignalBar';
import { useState } from 'react';

export default function PartnerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { partners, topics } = useAppStore();
  const partner = partners.find(p => p.id === id);
  const [showSigprint, setShowSigprint] = useState(null);

  if (!partner) return <div className="p-8 text-center text-gray-500">Partner not found</div>;

  return (
    <div className="px-4 py-5 pb-8 animate-fade-in">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-200 mb-5 transition-colors"><ArrowLeft size={18} />Back</button>

      {/* Partner Header */}
      <div className="glass p-5 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl" style={{background:partner.status==='active'?'rgba(16,185,129,0.12)':'rgba(249,115,22,0.1)', border:`1px solid ${partner.status==='active'?'rgba(16,185,129,0.25)':'rgba(249,115,22,0.2)'}`, color:partner.status==='active'?'#10b981':'#fb923c'}}>
            {partner.initial}
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-100">{partner.displayName}</h1>
            <p className="text-xs text-gray-500 font-mono">{partner.profileId}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">Linked {partner.linkedAt}</p>
          </div>
        </div>
      </div>

      {partner.status === 'pending' ? (
        <div className="glass p-6 text-center">
          <p className="text-gray-400 text-sm mb-2">Waiting for {partner.displayName} to share topics.</p>
          <p className="text-xs text-gray-500">Once you both share at least one topic, compatibility signals will appear here.</p>
        </div>
      ) : (
        <>
          {/* Sharing Controls */}
          <div className="mb-6">
            <h2 className="section-label mb-3">Topic Sharing</h2>
            <div className="space-y-2">
              {topics.filter(t=>t.completed).map(t => {
                const Icon = Icons[t.icon] || Icons.Circle;
                const shared = partner.youShare.includes(t.id);
                const mutual = partner.mutualTopics.includes(t.id);
                return (
                  <div key={t.id} className="glass-sm px-4 py-3 flex items-center gap-3">
                    <Icon size={16} style={{color:t.color}} />
                    <span className="text-sm text-gray-200 flex-1">{t.name}</span>
                    {mutual && <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">mutual</span>}
                    <button className={`p-1.5 rounded-lg transition-colors ${shared ? 'text-burner-400 bg-burner-500/10' : 'text-gray-600 bg-zinc-800/50'}`}>
                      {shared ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Compatibility */}
          <div>
            <h2 className="section-label mb-3">Compatibility</h2>
            <div className="space-y-3">
              {partner.mutualTopics.map(tid => {
                const topic = topics.find(t=>t.id===tid);
                const compat = partner.compatibility[tid];
                const sigs = partner.sigprints[tid];
                if (!topic || !compat) return null;
                const Icon = Icons[topic.icon] || Icons.Circle;
                return (
                  <div key={tid} className="glass p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <Icon size={18} style={{color:topic.color}} />
                      <span className="text-sm font-medium text-gray-100">{topic.name}</span>
                    </div>
                    <SignalBar score={compat.score} signal={compat.signal} />
                    <p className="text-xs text-gray-400 mt-2.5">{compat.summary}</p>

                    {sigs && (
                      <div className="mt-3">
                        <button onClick={() => setShowSigprint(showSigprint===tid ? null : tid)} className="flex items-center gap-1.5 text-[10px] text-zinc-500 hover:text-zinc-400 transition-colors">
                          <Lock size={10} />SIGPRINT details
                          <ChevronDown size={12} className={`transition-transform ${showSigprint===tid?'rotate-180':''}`}/>
                        </button>
                        {showSigprint===tid && (
                          <div className="mt-2 bg-zinc-900/80 rounded-lg p-3 space-y-1.5 border border-white/[0.03]">
                            <div><span className="text-[9px] text-zinc-600 uppercase tracking-wider">yours</span><p className="font-mono text-[11px] text-zinc-400 tracking-wider">{sigs.yours}</p></div>
                            <div><span className="text-[9px] text-zinc-600 uppercase tracking-wider">theirs</span><p className="font-mono text-[11px] text-zinc-400 tracking-wider">{sigs.theirs}</p></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
